import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import logoImage from '../assets/logo.png';
import { formatLibelleForDisplay } from '../utils/libelleFormatter';

/**
 * Page d'impression / visionnage de la liste des charges d'un client.
 * URL: /client/:id/charges/print?annee=YYYY&preview=1 (preview = visionnage sans impression auto)
 */
export default function ClientChargesListPrintPage() {
  const { id: clientId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [client, setClient] = useState(null);
  const [charges, setCharges] = useState([]);
  const [previewMode, setPreviewMode] = useState(false);
  const printDoneRef = useRef(false);

  const formatMontant = (montant) => {
    const value = parseFloat(montant);
    if (isNaN(value)) return '0,000';
    const absValue = Math.abs(value);
    return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(absValue);
  };

  const formatMontantWithSign = (montant) => {
    const value = parseFloat(montant || 0);
    const isNeg = value < 0;
    const base = formatMontant(Math.abs(value));
    return `${isNeg ? '-' : ''}${base}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${day}/${m}/${y}`;
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const annee = params.get('annee') || new Date().getFullYear();
    const isPreview = params.get('preview') === '1';
    setPreviewMode(isPreview);

    const runAsync = async () => {
      if (!clientId) {
        setError('Client non spécifié');
        setLoading(false);
        return;
      }
      try {
        let sorted = [];
        const [clientRes, chargesRes] = await Promise.all([
          fetch(`/api/clients/${clientId}`),
          fetch(`/api/charges-mensuelles/client/${clientId}?annee=${annee}`)
        ]);
        const clientData = await clientRes.json();
        const chargesData = await chargesRes.json();

        if (!clientData.success || !clientData.client) {
          setError('Client non trouvé');
          setLoading(false);
          return;
        }
        setClient(clientData.client);

        if (chargesData.success && Array.isArray(chargesData.charges)) {
          sorted = [...chargesData.charges].sort((a, b) => {
            const dateA = a.date ? new Date(a.date) : (a.date_creation ? new Date(a.date_creation) : new Date(0));
            const dateB = b.date ? new Date(b.date) : (b.date_creation ? new Date(b.date_creation) : new Date(0));
            return dateA - dateB;
          });
          setCharges(sorted);
        } else {
          setCharges([]);
        }
        setLoading(false);

        if (!isPreview) {
          setTimeout(() => doPrint(clientData.client, sorted, annee), 100);
        }
      } catch (e) {
        setError(e.message || 'Erreur');
        setLoading(false);
      }
    };
    runAsync();
  }, [clientId]);

  function computeSoldeCumulatif(chargesList, index) {
    const charge = chargesList[index];
    if (charge.isPrecedent) return parseFloat(charge.solde_restant || 0);
    let stockInitial = 0;
    const soldeReporte = chargesList.find(c => c.isPrecedent);
    if (soldeReporte) stockInitial = parseFloat(soldeReporte.solde_restant || 0);
    let solde = stockInitial;
    for (let i = 0; i <= index; i++) {
      const c = chargesList[i];
      if (c.isPrecedent) continue;
      solde += parseFloat(c.avance || 0) - parseFloat(c.montant || 0);
    }
    return solde;
  }

  function doPrint(clientObj, chargesList, annee) {
    if (printDoneRef.current) return;
    printDoneRef.current = true;

    const clientName = `${clientObj.nom || ''} ${clientObj.prenom || ''}`.trim();
    const now = new Date();
    const dateImpression = now.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const heureImpression = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    const totalAvance = chargesList.reduce((s, c) => s + parseFloat(c.avance || 0), 0);
    const totalMontant = chargesList.reduce((s, c) => s + parseFloat(c.montant || 0), 0);
    const soldeReporte = chargesList.find(c => c.isPrecedent);
    let totalReste = soldeReporte ? parseFloat(soldeReporte.solde_restant || 0) : 0;
    totalReste += chargesList.reduce((s, c) => {
      if (c.isPrecedent) return s;
      return s + parseFloat(c.avance || 0) - parseFloat(c.montant || 0);
    }, 0);

    const rowsHtml = chargesList.length > 0
      ? chargesList.map((charge, i) => {
          const soldeCumul = computeSoldeCumulatif(chargesList, i);
          const dateDisplay = charge.date ? formatDate(charge.date) : formatDate(charge.date_creation);
          const libelleDisplay = formatLibelleForDisplay(charge.libelle);
          return `<tr>
            <td>${dateDisplay}</td>
            <td>${libelleDisplay}</td>
            <td>${formatMontant(charge.montant)}</td>
            <td>${formatMontant(charge.avance)}</td>
            <td>${formatMontantWithSign(soldeCumul)}</td>
          </tr>`;
        }).join('')
      : '<tr><td colspan="5" style="text-align:center;color:#666;font-style:italic;">Aucune charge pour cette année</td></tr>';

    const printContent = `<!DOCTYPE html>
<html>
<head>
  <title>Liste des charges - ${clientName}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 12px; font-size: 11px; }
    .header { text-align: center; margin-bottom: 10px; }
    .logo-container img { height: 50px; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0; border: 1px solid #000; }
    th, td { border: 1px solid #000; padding: 4px 6px; }
    thead th { font-weight: bold; background: #f9f9f9; }
    .total-row { font-weight: bold; background: #f9f9f9; }
  </style>
</head>
<body>
  <div style="text-align: right; margin-bottom: 10px; font-size: 11px; font-weight: bold;">
    Date d'impression : ${dateImpression}, ${heureImpression}
  </div>
  <div class="header">
    <img src="${logoImage}" alt="Logo" style="height: 50px;" />
  </div>
  <h2 style="margin: 12px 0;">Liste des charges client — ${clientName}</h2>
  <p style="margin-bottom: 10px;"><strong>Année :</strong> ${annee}</p>
  <table>
    <thead>
      <tr><th>Date</th><th>Libellé</th><th>Montant</th><th>Avance</th><th>Reste</th></tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
    <tfoot>
      <tr class="total-row">
        <td colspan="2">TOTAL</td>
        <td>${formatMontant(chargesList.reduce((s, c) => s + parseFloat(c.montant || 0), 0))}</td>
        <td>${formatMontant(totalAvance)}</td>
        <td>${formatMontantWithSign(totalReste)}</td>
      </tr>
    </tfoot>
  </table>
</body>
</html>`;

    try {
      document.open();
      document.write(printContent);
      document.close();
      window.onload = () => { window.print(); };
    } catch (_) {
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(printContent);
        w.document.close();
        w.onload = () => { w.print(); w.close(); };
      }
    }
  }

  const handlePrintFromPreview = () => window.print();

  if (loading) return <div className="container py-5">Préparation{previewMode ? ' de la prévisualisation' : ' de l\'impression'}...</div>;
  if (error) return <div className="container py-5 text-danger">{error}</div>;

  if (previewMode && client && !loading) {
    const totalAvance = charges.reduce((s, c) => s + parseFloat(c.avance || 0), 0);
    const totalMontant = charges.reduce((s, c) => s + parseFloat(c.montant || 0), 0);
    const soldeReporte = charges.find(c => c.isPrecedent);
    let totalReste = soldeReporte ? parseFloat(soldeReporte.solde_restant || 0) : 0;
    totalReste += charges.reduce((s, c) => {
      if (c.isPrecedent) return s;
      return s + parseFloat(c.avance || 0) - parseFloat(c.montant || 0);
    }, 0);
    const params = new URLSearchParams(window.location.search);
    const annee = params.get('annee') || new Date().getFullYear();
    const clientName = `${client.nom || ''} ${client.prenom || ''}`.trim();

    return (
      <div className="container py-4">
        <style>{`@media print { .client-charges-print-no-print { display: none !important; } }`}</style>
        <div className="client-charges-print-no-print mb-3 d-flex gap-2">
          <button type="button" className="btn btn-primary" onClick={handlePrintFromPreview}>
            <i className="fas fa-print me-1"></i> Imprimer
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => window.close()}>
            Fermer
          </button>
        </div>
        <h2 className="mb-3">Liste des charges client — {clientName}</h2>
        <p className="mb-3"><strong>Année :</strong> {annee}</p>
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Date</th>
                <th>Libellé</th>
                <th>Montant</th>
                <th>Avance</th>
                <th>Reste</th>
              </tr>
            </thead>
            <tbody>
              {charges.length > 0 ? charges.map((charge, i) => {
                const soldeCumul = computeSoldeCumulatif(charges, i);
                return (
                  <tr key={charge.id || i}>
                    <td>{charge.date ? formatDate(charge.date) : formatDate(charge.date_creation)}</td>
                    <td>{formatLibelleForDisplay(charge.libelle)}</td>
                    <td>{formatMontant(charge.montant)}</td>
                    <td>{formatMontant(charge.avance)}</td>
                    <td>{formatMontantWithSign(soldeCumul)}</td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="5" className="text-center text-muted fst-italic">Aucune charge pour cette année</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="table-secondary fw-bold">
                <td colSpan="2">TOTAL</td>
                <td>{formatMontant(totalMontant)}</td>
                <td>{formatMontant(totalAvance)}</td>
                <td>{formatMontantWithSign(totalReste)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  }

  return null;
}
